import React, { useRef, useEffect, useState } from 'react';
import { Eraser, Loader2, CheckCircle2 } from 'lucide-react';

interface SignaturePadProps {
  onSignatureChange: (dataUrl: string | null) => void;
  loading?: boolean;
  onSubmit?: () => void;
  termsAccepted: boolean;
  onTermsToggle: (v: boolean) => void;
  signerName: string;
  onSignerNameChange: (v: string) => void;
  // Recapiti dove ricevere il contratto firmato (precompilati, modificabili).
  notifyEmail: string;
  onNotifyEmailChange: (v: string) => void;
  notifyPhonePrefix: string;
  onNotifyPhonePrefixChange: (v: string) => void;
  notifyPhoneNumber: string;
  onNotifyPhoneNumberChange: (v: string) => void;
}

/**
 * Canvas firma mobile-first: tap target ampio, ratio responsive, controlli accessibili.
 * Sopra il canvas: nome firmatario + checkbox accettazione termini.
 * Sotto: clear + CTA primario gradient Flusia.
 */
export function SignaturePad({
  onSignatureChange,
  loading,
  onSubmit,
  termsAccepted,
  onTermsToggle,
  signerName,
  onSignerNameChange,
  notifyEmail,
  onNotifyEmailChange,
  notifyPhonePrefix,
  onNotifyPhonePrefixChange,
  notifyPhoneNumber,
  onNotifyPhoneNumberChange,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Setup canvas size con DPR per evitare blur su retina
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#37352F';
  }, []);

  const getCoords = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.closePath();
    const dataUrl = canvas.toDataURL();
    setHasSignature(true);
    onSignatureChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  const canSubmit = hasSignature && termsAccepted && signerName.trim().length >= 2;

  return (
    <section className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <div className="px-6 sm:px-8 py-5 border-b border-black/[0.06]">
        <h2 className="text-base font-semibold text-[#37352F]">Firma il documento</h2>
        <p className="text-xs text-[#9D9892] mt-0.5">
          Inserisci il tuo nome, accetta i termini, poi traccia la firma qui sotto.
        </p>
      </div>

      <div className="px-6 sm:px-8 py-5 space-y-5">
        {/* Nome firmatario */}
        <div>
          <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
            Il tuo nome
          </label>
          <input
            id="signer-name-input"
            type="text"
            value={signerName}
            onChange={(e) => onSignerNameChange(e.target.value)}
            placeholder="Nome e cognome"
            className="block w-full px-4 py-3 text-base text-[#37352F] bg-black/[0.03] border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow placeholder-[#9D9892]"
          />
        </div>

        {/* Recapiti dove ricevere il contratto firmato (precompilati, modificabili) */}
        <div className="rounded-xl border border-[#D6336C]/15 bg-[#D6336C]/[0.03] p-4">
          <div className="text-xs font-semibold text-[#37352F] mb-3">
            Dove vuoi ricevere il contratto firmato?
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={notifyEmail}
                onChange={(e) => onNotifyEmailChange(e.target.value)}
                placeholder="tuaemail@esempio.com"
                className="block w-full px-4 py-3 text-base text-[#37352F] bg-white border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow placeholder-[#9D9892]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
                WhatsApp
              </label>
              <div className="flex gap-2">
                <select
                  value={notifyPhonePrefix}
                  onChange={(e) => onNotifyPhonePrefixChange(e.target.value)}
                  className="px-3 py-3 text-base text-[#37352F] bg-white border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow"
                >
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+41">🇨🇭 +41</option>
                  <option value="+43">🇦🇹 +43</option>
                  <option value="+32">🇧🇪 +32</option>
                  <option value="+31">🇳🇱 +31</option>
                  <option value="+351">🇵🇹 +351</option>
                  <option value="+30">🇬🇷 +30</option>
                  <option value="+45">🇩🇰 +45</option>
                  <option value="+46">🇸🇪 +46</option>
                  <option value="+47">🇳🇴 +47</option>
                  <option value="+358">🇫🇮 +358</option>
                  <option value="+48">🇵🇱 +48</option>
                  <option value="+36">🇭🇺 +36</option>
                  <option value="+420">🇨🇿 +420</option>
                  <option value="+7">🇷🇺 +7</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+82">🇰🇷 +82</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+64">🇳🇿 +64</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+52">🇲🇽 +52</option>
                  <option value="+54">🇦🇷 +54</option>
                  <option value="+27">🇿🇦 +27</option>
                </select>
                <input
                  type="tel"
                  value={notifyPhoneNumber}
                  onChange={(e) => onNotifyPhoneNumberChange(e.target.value)}
                  placeholder="333 1234567"
                  className="flex-1 px-4 py-3 text-base text-[#37352F] bg-white border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow placeholder-[#9D9892]"
                />
              </div>
            </div>
            <p className="text-xs text-[#6B6762]">
              Riceverai il contratto firmato ai recapiti indicati.
            </p>
          </div>
        </div>

        {/* Canvas */}
        <div>
          <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
            Firma autografa
          </label>
          <div
            className="relative bg-[#FBFBFA] border border-dashed border-black/[0.15] rounded-xl overflow-hidden"
            style={{ touchAction: 'none' }}
          >
            <canvas
              ref={canvasRef}
              className="w-full h-48 cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={endDrawing}
              onMouseLeave={endDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={endDrawing}
            />
            {!hasSignature && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-sm text-[#9D9892]">Traccia qui la tua firma</span>
              </div>
            )}
            <button
              type="button"
              onClick={clear}
              disabled={!hasSignature}
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#6B6762] bg-white/90 hover:bg-white rounded-lg border border-black/[0.08] shadow-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Eraser className="h-3.5 w-3.5" />
              Cancella
            </button>
          </div>
        </div>

        {/* Checkbox termini */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <span className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => onTermsToggle(e.target.checked)}
              className="peer sr-only"
            />
            <span
              className="block w-5 h-5 rounded-md border-2 border-black/[0.15] bg-white peer-checked:border-transparent transition-colors"
              style={{
                background: termsAccepted
                  ? 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)'
                  : undefined,
              }}
            >
              {termsAccepted && <CheckCircle2 className="h-5 w-5 text-white -m-0.5" />}
            </span>
          </span>
          <span className="text-sm text-[#37352F] leading-snug select-none group-hover:text-[#37352F]/80">
            Ho letto e accetto i <strong>termini e le condizioni</strong> riportati sopra. Confermo
            di voler firmare elettronicamente questo documento.
          </span>
        </label>

        {/* CTA submit */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || loading}
          className="w-full inline-flex justify-center items-center gap-2 px-5 py-3.5 text-base font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          style={{
            background:
              !canSubmit || loading
                ? '#9D9892'
                : 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)',
          }}
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Elaborazione firma…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-5 w-5" />
              Firma e invia
            </>
          )}
        </button>

        {!canSubmit && !loading && (
          <div className="text-xs text-[#9D9892] text-center">
            {!signerName.trim() && '• Inserisci il tuo nome'}
            {signerName.trim() && !hasSignature && '• Traccia la firma sul canvas'}
            {hasSignature && !termsAccepted && '• Accetta i termini per procedere'}
          </div>
        )}
      </div>
    </section>
  );
}
